import { auth, idToken } from './firebase'
import { listOf } from './types'
import type {
  Comment,
  DocDetail,
  DocSummary,
  Facet,
  Maybe,
  Me,
  Role,
  Scope,
  SearchResponse,
  Token,
  TreeService,
  User,
  Visibility,
} from './types'

const BASE = (import.meta.env.VITE_API_BASE as string | undefined) ?? '/doc'
const API = `${BASE}/api/v1`

/** Machine-readable codes the backend returns alongside a human message. */
export type ErrorCode =
  | 'unauthorized'
  | 'forbidden'
  | 'not_found'
  | 'staff_only'
  | 'bad_input'
  | (string & {})

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }
  /** Internal doc requested by someone without a staff role. */
  get isStaffOnly() {
    return this.code === 'staff_only' || (this.status === 403 && this.code === 'forbidden')
  }
}

interface Options {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  /** Reject before sending if nobody is signed in. */
  requireAuth?: boolean
  /** Never send a token, even if someone is signed in: the route is how a session begins. */
  anonymous?: boolean
  signal?: AbortSignal
}

async function request<T>(path: string, opts: Options = {}): Promise<T> {
  const { method = 'GET', body, requireAuth = false, anonymous = false, signal } = opts

  const headers: Record<string, string> = {}
  const user = anonymous ? null : auth.currentUser
  if (user) headers.Authorization = `Bearer ${await idToken(user)}`
  else if (requireAuth) throw new ApiError(401, 'unauthorized', 'Sign in to continue.')

  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const send = (h: Record<string, string>) =>
    fetch(`${API}${path}`, {
      method,
      headers: h,
      signal,
      body: body === undefined ? undefined : JSON.stringify(body),
    })

  let res = await send(headers)

  // An optional-auth route must still serve public data when the caller's token is
  // rejected — deactivated account, revoked user, clock skew. Otherwise one bad
  // token blanks the whole site instead of degrading it to the anonymous view.
  if (res.status === 401 && !requireAuth && headers.Authorization) {
    const { Authorization: _dropped, ...anonymous } = headers
    res = await send(anonymous)
  }

  if (res.status === 204) return undefined as T

  // An error body is *usually* `{error, error_code}` — but a proxy or a 502 can
  // hand us HTML, so never assume the response parses.
  const payload = await res.json().catch(() => null)

  if (!res.ok) {
    const code = (payload?.error_code as ErrorCode | undefined) ?? `http_${res.status}`
    const message = (payload?.error as string | undefined) ?? res.statusText
    throw new ApiError(res.status, code, message)
  }
  return payload as T
}

const qs = (params: Record<string, string | undefined>) => {
  const sp = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) if (v) sp.set(k, v)
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export const api = {
  /** Call right after sign-in; upserts the user and syncs the `role` claim. */
  me: () => request<Me>('/me', { requireAuth: true }),

  /**
   * Sign-in with SightHub starts with a full browser navigation here, never a fetch.
   * The backend returns only to pages of the wiki itself (`400 bad_return` for any
   * other `returnTo`), with `#sso_code=…` or `#sso_error=…` — see lib/sso.ts.
   */
  ssoStartUrl: (returnTo: string) => `${API}/auth/sso/start?return=${encodeURIComponent(returnTo)}`,

  /** The code is single-use and lives two minutes; past either it is `400 bad_code`. */
  ssoExchange: (code: string, signal?: AbortSignal) =>
    request<{ custom_token: string }>('/auth/sso/exchange', {
      method: 'POST',
      body: { code },
      anonymous: true,
      signal,
    }).then((r) => r.custom_token),

  version: async () => {
    const res = await fetch(`${BASE}/version`)
    return (await res.json()) as { version: string }
  },

  // Empty lists come back as `null`, not `[]`, throughout the API. Every list-shaped
  // response is normalised here so no caller has to remember it.
  tree: (signal?: AbortSignal) =>
    request<{ tree: Maybe<TreeService> }>('/tree', { signal }).then((r) => listOf(r.tree)),

  docs: (f: { service?: string; topic?: string; visibility?: Visibility } = {}) =>
    request<{ docs: Maybe<DocSummary> }>(`/docs${qs(f)}`).then((r) => listOf(r.docs)),

  doc: (id: number, signal?: AbortSignal) => request<DocDetail>(`/docs/${id}`, { signal }),

  /**
   * Create or replace a doc from a Markdown file with YAML frontmatter — the same
   * artefact the IDE machine uploads, so a page authored here is byte-identical to
   * one authored in an editor.
   *
   * The documented shape is multipart with an `md` field. If the server rejects that
   * form we retry the identical bytes as JSON, because the write path is the one
   * place where guessing wrong silently loses the author's work.
   */
  saveDoc: async (md: string, opts: { id?: number; reindex?: boolean } = {}): Promise<DocDetail> => {
    const { id, reindex = true } = opts
    const path = `${id ? `/docs/${id}` : '/docs'}${reindex ? '' : '?reindex=0'}`
    const method = id ? 'PUT' : 'POST'

    const user = auth.currentUser
    if (!user) throw new ApiError(401, 'unauthorized', 'Sign in to continue.')
    const authorization = `Bearer ${await idToken(user)}`

    const form = new FormData()
    form.append('md', new Blob([md], { type: 'text/markdown' }), 'doc.md')

    let res = await fetch(`${API}${path}`, {
      method,
      headers: { Authorization: authorization },
      body: form,
    })

    if (res.status === 400 || res.status === 415) {
      res = await fetch(`${API}${path}`, {
        method,
        headers: { Authorization: authorization, 'Content-Type': 'application/json' },
        body: JSON.stringify({ md }),
      })
    }

    const payload = await res.json().catch(() => null)
    if (!res.ok) {
      throw new ApiError(
        res.status,
        (payload?.error_code as ErrorCode) ?? `http_${res.status}`,
        (payload?.error as string) ?? res.statusText,
      )
    }
    return payload as DocDetail
  },

  /**
   * Upload an image next to a page. The tier is taken from the doc when `docId` is
   * known, so a screenshot for an internal page cannot be written into `public/`
   * even by asking. Without a doc yet, the backend defaults to `internal`.
   *
   * The returned `filename` is deliberately relative: dropping it into the Markdown
   * as `![](name.png)` keeps the reference tier-agnostic, and the reader resolves it
   * through Storage rules (see lib/page.ts).
   */
  uploadImage: async (
    file: File,
    target:
      | { docId: number }
      | { service: string; topic: string; slug: string; visibility: Visibility },
  ): Promise<{ filename: string; path: string; url?: string }> => {
    const user = auth.currentUser
    if (!user) throw new ApiError(401, 'unauthorized', 'Sign in to continue.')

    const form = new FormData()
    form.append('file', file)
    if ('docId' in target) {
      form.append('doc_id', String(target.docId))
    } else {
      form.append('service', target.service)
      form.append('topic', target.topic)
      form.append('slug', target.slug)
      form.append('visibility', target.visibility)
    }

    const res = await fetch(`${API}/uploads`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${await idToken(user)}` },
      body: form,
    })

    const payload = await res.json().catch(() => null)
    if (!res.ok) {
      throw new ApiError(
        res.status,
        (payload?.error_code as ErrorCode) ?? `http_${res.status}`,
        (payload?.error as string) ?? res.statusText,
      )
    }
    return payload as { filename: string; path: string; url?: string }
  },

  /** Destruction is admin-only, enforced by the backend. */
  deleteDoc: (id: number) => request<unknown>(`/docs/${id}`, { method: 'DELETE', requireAuth: true }),

  /** Rebuild the Pagefind index for a whole tier. Admin-only, and slow. */
  reindex: (tier: Visibility) =>
    request<unknown>(`/reindex?tier=${tier}`, { method: 'POST', requireAuth: true }),

  search: (
    f: { q?: string; tag?: string; service?: string },
    signal?: AbortSignal,
  ): Promise<{ count: number; results: DocSummary[]; facets: Facet[] }> =>
    request<SearchResponse>(`/search${qs(f)}`, { signal }).then((r) => ({
      count: r.count,
      results: listOf(r.results),
      facets: listOf(r.facets),
    })),

  comments: (docId: number, signal?: AbortSignal) =>
    request<{ comments: Maybe<Comment> }>(`/docs/${docId}/comments`, { signal }).then((r) =>
      listOf(r.comments),
    ),

  addComment: (docId: number, body: string, parentId?: number) =>
    request<Comment>(`/docs/${docId}/comments`, {
      method: 'POST',
      requireAuth: true,
      body: parentId ? { body, parent_id: parentId } : { body },
    }),

  /** Re-sending the same value toggles the vote off. Returns the net score. */
  vote: (docId: number, value: 1 | -1) =>
    request<{ votes: number }>(`/docs/${docId}/vote`, {
      method: 'POST',
      requireAuth: true,
      body: { value },
    }).then((r) => r.votes),

  favorite: (docId: number) =>
    request<{ favorited: boolean }>(`/favorites/${docId}`, {
      method: 'POST',
      requireAuth: true,
    }).then((r) => r.favorited),

  unfavorite: (docId: number) =>
    request<{ favorited: boolean }>(`/favorites/${docId}`, {
      method: 'DELETE',
      requireAuth: true,
    }).then((r) => r.favorited),

  favorites: (signal?: AbortSignal) =>
    request<{ docs: Maybe<DocSummary> }>('/favorites', { requireAuth: true, signal }).then((r) =>
      listOf(r.docs),
    ),

  admin: {
    users: (signal?: AbortSignal) =>
      request<{ users: Maybe<User> }>('/users', { requireAuth: true, signal }).then((r) =>
        listOf(r.users),
      ),

    updateUser: (id: number, patch: { role?: Role; active?: boolean }) =>
      request<{ updated: number }>(`/users/${id}`, { method: 'PUT', requireAuth: true, body: patch }),

    /**
     * Register a machine identity. This endpoint only ever creates machines —
     * people appear solely through Google sign-in — so `machine` is not sent.
     * `role` defaults to `developer` and must be `user`, `employee` or `developer`.
     */
    createMachine: (m: { email: string; role?: Role }) =>
      request<User>('/users', { method: 'POST', requireAuth: true, body: m }),

    /** Creating a service stays admin-only. */
    createService: (s: { slug: string; title: string; description?: string; ord?: number }) =>
      request<unknown>('/services', { method: 'POST', requireAuth: true, body: s }),

    /**
     * `path` is the full slash path from the service root; the backend creates every
     * missing level along the way, so `doctor-desk/exams/subexams` needs one call.
     */
    createTopic: (t: { service: string; path: string; title: string; ord?: number }) =>
      request<unknown>('/topics', { method: 'POST', requireAuth: true, body: t }),

    /**
     * `raw_token` comes back exactly once, here. It is never retrievable again.
     *
     * Pass `forEmail` to mint a token that acts as a *machine*: the backend records
     * the caller as `created_by`, so a revision is attributable both to the machine
     * that made it and to the admin who authorised it. Aiming `for_email` at a person
     * is refused (`not_machine`) — that would let an admin impersonate a colleague.
     */
    createToken: (opts: { name?: string; forEmail?: string; scopes?: Scope[]; expiresAt?: string } = {}) =>
      request<Token>('/tokens', {
        method: 'POST',
        requireAuth: true,
        body: {
          ...(opts.name ? { name: opts.name } : {}),
          ...(opts.forEmail ? { for_email: opts.forEmail } : {}),
          // Omitting `scopes` makes the backend default to ["write"], so an explicit
          // empty array is meaningful and must survive.
          ...(opts.scopes === undefined ? {} : { scopes: opts.scopes }),
          ...(opts.expiresAt ? { expires_at: opts.expiresAt } : {}),
        },
      }),

    tokens: (all = false) =>
      request<{ tokens: Maybe<Token> }>(`/tokens${all ? '?all=1' : ''}`, { requireAuth: true }).then(
        (r) => listOf(r.tokens),
      ),

    revokeToken: (id: number) =>
      request<unknown>(`/tokens/${id}`, { method: 'DELETE', requireAuth: true }),
  },
}
